import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { isResearcher } from 'src/utils/checkPermissions';
import { Fund } from 'src/components/Company/CompanyBuyer/Fund';

import UrlMaker from '../../../routing/UrlMaker';
import connectOptions, { mergeProps } from '../../../utils/connectOptions';
import {
  openContact,
  handleUpdateCompany,
  getFunds,
  startEditDetailEntity,
  saveInfo,
  closeValidationError,
  updateFundDate,
  updateFundSort,
} from '../../../actions/company/buyer/fund';
import { getChanged } from '../../../utils/ChangeSpy';
import { PrettyErrorProvider } from '../../../contexts/PrettyErrorContext';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

const FIELDS = {
  dateAcquired: 'date_acquired',
  legalName: 'legal_name',
  dsplIndustry: 'dspl_industry',
  currentStatus: 'current_status',
  highStatus: 'high_status',
};

const FundContainer = ({
  fund,
  user,
  companyId,
  inputErrors,
  isDuplicateCompany,
  match,
  getFunds,
  saveInfo,
  closeValidationError,
  handleUpdateCompany,
  updateFundDate,
  updateFundSort,
}) => {
  const { addOnSaveCallback, removeOnSaveCallback } = useEntityEventContext();
  const gridApiRef = useRef(null);

  const getCanEditData = useCallback(() => !isDuplicateCompany, [isDuplicateCompany]);

  const handleSubmit = useCallback(
    event => {
      if (!getCanEditData()) return;

      event.preventDefault();
      if (fund.get('isValid')) {
        const body = getChanged(fund);
        const { companyId: paramCompanyId } = match.params;

        if (Object.keys(body).length) {
          saveInfo({ companyId: paramCompanyId, body });
        }
      }
    },
    [getCanEditData, fund, match, saveInfo],
  );

  useEffect(() => {
    const sort = fund.get('sort');

    getFunds({ companyId, sort });
    addOnSaveCallback(handleSubmit);
    if (gridApiRef.current) {
      gridApiRef.current.sizeColumnsToFit();
    }

    return () => {
      removeOnSaveCallback(handleSubmit);
    };
  }, []);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleChange = useCallback(
    ({ name, value }) => {
      if (!getCanEditData()) return;

      handleUpdateCompany({ name, value });
    },
    [getCanEditData, handleUpdateCompany],
  );

  const handleFormChange = useCallback(
    ({ target: { name, type, value, checked } }) => {
      if (type === 'checkbox') return handleChange({ name, value: checked });

      return handleChange({ name, value });
    },
    [handleChange],
  );

  const getSort = useCallback((colId, sort) => (colId ? `${sort === 'asc' ? '' : '-'}${FIELDS[colId]}` : ''), []);

  const handleSort = useCallback(
    data => {
      if (!getCanEditData()) return;

      if (data.length) {
        const { colId, sort } = data[0];
        const sortExpression = getSort(colId, sort);

        updateFundSort(sortExpression);
        getFunds({ companyId, sort: sortExpression });
      }
    },
    [getCanEditData, getSort, updateFundSort, getFunds, companyId],
  );

  const handleGetNext = useCallback(page => getFunds({ companyId, sort: fund.get('sort'), page }), [
    getFunds,
    companyId,
    fund,
  ]);

  const handleCellClick = useCallback(
    data => {
      if (!getCanEditData()) return;

      if (data.colDef.field === 'dateAcquired') {
        gridApiRef.current.startEditingCell({
          rowIndex: data.node.rowIndex,
          colKey: data.colDef.field,
        });
      } else {
        gridApiRef.current.stopEditing(true);
      }
    },
    [getCanEditData],
  );

  const handleCellValueChange = useCallback(
    event => {
      if (!getCanEditData()) return;

      const { data, node } = event;

      if (
        (event.newValue && event.oldValue && event.newValue.diff(event.oldValue)) ||
        event.newValue !== event.oldValue
      ) {
        updateFundDate({
          index: node.childIndex,
          companyId: data.entityId,
          value: data.dateAcquired,
        });
      }
    },
    [getCanEditData, updateFundDate],
  );

  const handleRef = useCallback(params => {
    gridApiRef.current = params.api;
  }, []);

  const handleRowDoubleClick = useCallback(
    ({ data }) => {
      if (!getCanEditData()) return;

      window.open(
        UrlMaker.create('/company/:companyId/target').mapParams({
          companyId: data.entityId,
        }),
      );
    },
    [getCanEditData],
  );

  const isResearcherUser = isResearcher(user.getIn(['roles', 0, 'slug'], null));

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <Fund
        canEditData={getCanEditData()}
        fund={fund}
        isResearcherUser={isResearcherUser}
        onCellClick={handleCellClick}
        onCellValueChange={handleCellValueChange}
        onChange={handleFormChange}
        onGetNext={handleGetNext}
        onRef={handleRef}
        onRowDoubleClick={handleRowDoubleClick}
        onSort={handleSort}
      />
    </PrettyErrorProvider>
  );
};

FundContainer.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    companyId: state.targetCompany.info.getIn(['info', 'id']),
    fund: state.targetCompany.buyer.fund,
    inputErrors: state.targetCompany.buyer.fund.get('inputErrors'),
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
    user: state.auth.get('user', null),
  };
}

export { FundContainer };
export default connect(
  mapStateToProps,
  {
    openContact,
    handleUpdateCompany,
    startEditDetailEntity,
    saveInfo,
    closeValidationError,
    getFunds,
    updateFundDate,
    updateFundSort,
  },
  mergeProps,
  connectOptions,
)(memo(FundContainer));
